import React from "react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../ui/dialog";
import { Key } from "lucide-react";

/**
 * PasswordResetModal — Diálogo de cambio de contraseña para usuarios locales.
 *
 * Extraído 1:1 desde `pages/UsersPage.js`. Solo aplica a usuarios con `auth_provider === "local"`.
 * La lógica de reseteo permanece en la página (`submitPasswordReset`); este componente es
 * únicamente presentacional.
 *
 * @param {Object|null} props.passwordModalUser    Usuario objetivo (null = cerrado).
 * @param {Function}    props.setPasswordModalUser  Setter que cierra el diálogo al pasar null.
 * @param {string}      props.newPassword           Nueva contraseña.
 * @param {Function}    props.setNewPassword        Setter de la nueva contraseña.
 * @param {string}      props.confirmPassword       Confirmación de contraseña.
 * @param {Function}    props.setConfirmPassword    Setter de la confirmación.
 * @param {Function}    props.submitPasswordReset   Envía el cambio de contraseña.
 * @param {boolean}     props.resettingPassword     Estado de envío en curso (deshabilita el botón).
 */
export const PasswordResetModal = ({
  passwordModalUser,
  setPasswordModalUser,
  newPassword,
  setNewPassword,
  confirmPassword,
  setConfirmPassword,
  submitPasswordReset,
  resettingPassword,
}) => {
  return (
      <Dialog open={!!passwordModalUser} onOpenChange={(o) => !o && setPasswordModalUser(null)}>
        <DialogContent className="bg-[#0A0A35] border-[#1E1E5F] text-white max-w-md" data-testid="password-reset-modal">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <Key className="h-5 w-5 text-amber-400" />
              Cambiar contraseña
            </DialogTitle>
            <DialogDescription className="text-slate-400">
              Usuario: <span className="text-white font-semibold">{passwordModalUser?.name}</span> ({passwordModalUser?.email})
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 mt-2">
            <div className="space-y-2">
              <Label className="text-slate-300">Nueva contraseña</Label>
              <Input
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                placeholder="Mínimo 8 caracteres"
                className="bg-[#0A0A35] border-[#1E1E5F] text-white"
                data-testid="new-password-input"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-slate-300">Confirmar contraseña</Label>
              <Input
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="Repite la nueva contraseña"
                className="bg-[#0A0A35] border-[#1E1E5F] text-white"
                data-testid="confirm-password-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setPasswordModalUser(null)}
              className="border-[#1E1E5F] text-slate-300"
            >
              Cancelar
            </Button>
            <Button
              onClick={submitPasswordReset}
              disabled={resettingPassword || !newPassword || !confirmPassword}
              className="bg-[#42E695] text-[#04042C] hover:bg-[#34D399]"
              data-testid="submit-password-reset-btn"
            >
              {resettingPassword ? "Guardando..." : "Guardar contraseña"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
  );
};
